import { ThemeEvents, CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * SRT Sticky Add-To-Cart
 * - Shows when the main buy-buttons block scrolls out of view
 * - Adds current variant to cart via Theme.routes.cart_add_url
 */
class SrtStickyATC {
  constructor() {
    this.bar = document.getElementById('srt-sticky-atc');
    if (!this.bar) return;

    this.button = this.bar.querySelector('[data-srt-sticky-atc-button]');
    this.price = this.bar.querySelector('[data-srt-sticky-price]');
    this.errorEl = this.bar.querySelector('[data-srt-sticky-error]');

    this.currentVariantId = this.bar.dataset.initialVariantId || null;
    this.isAvailable = true;

    this.anchor = this.#findAnchor();
    this.#bind();
    this.#observe();
    this.#syncPriceFromPage();
  }

  #findAnchor() {
    // Prefer the product-form component (buy buttons) as our "in view" anchor
    return (
      document.querySelector('product-form-component') ||
      document.querySelector('[data-testid="product-information"]') ||
      document.querySelector('main')
    );
  }

  #bind() {
    document.addEventListener(ThemeEvents.variantUpdate, (e) => {
      const v = e?.detail?.resource;
      if (!v) return;

      this.currentVariantId = String(v.id || '');
      this.isAvailable = Boolean(v.available);

      // Keep the sticky bar price in sync with the page's updated price
      this.#syncPriceFromPage();

      if (this.button) {
        this.button.disabled = !this.isAvailable;
        this.button.setAttribute('aria-disabled', String(!this.isAvailable));
      }
    });

    if (this.button) {
      this.button.addEventListener('click', () => this.#addToCart());
    }
  }

  #observe() {
    if (!this.anchor) {
      this.bar.classList.add('is-visible');
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;

        // Show sticky bar when anchor is NOT visible
        if (entry.isIntersecting) {
          this.bar.classList.remove('is-visible');
        } else {
          this.bar.classList.add('is-visible');
        }
      },
      { root: null, threshold: 0.15 }
    );

    io.observe(this.anchor);
  }

  #syncPriceFromPage() {
    // Ritual uses <product-price> on product pages
    const pagePrice = document.querySelector('product-price');
    if (!pagePrice || !this.price) return;

    // Clone the updated price markup for consistent formatting
    this.price.innerHTML = pagePrice.innerHTML;
  }

  #showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || '';
    this.errorEl.hidden = !message;
  }

  async #addToCart() {
    this.#showError('');

    if (!this.currentVariantId) {
      this.#showError('Please select an option.');
      return;
    }

    if (!this.isAvailable) {
      this.#showError('This option is currently sold out.');
      return;
    }

    // If the main product form is visible, let the native product-form handle it
    const mainAtcBtn = document.querySelector('#MainContent button[type="submit"], #MainContent [data-testid="add-to-cart"]');
    const mainFormInView = document.querySelector('product-form-component')?.getBoundingClientRect?.().top < window.innerHeight;

    if (mainAtcBtn && mainFormInView) {
      mainAtcBtn.click();
      return;
    }

    try {
      const formData = new FormData();
      formData.append('id', this.currentVariantId);
      formData.append('quantity', '1');

      const fetchCfg = fetchConfig('javascript', { body: formData });

      const res = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });

      const json = await res.json();

      if (json?.status) {
        this.dispatchCartError(json);
        this.#showError(json.message || 'Unable to add to cart.');
        return;
      }

      document.dispatchEvent(
        new CartAddEvent({}, this.currentVariantId, {
          source: 'srt-sticky-atc',
          itemCount: 1,
          variantId: this.currentVariantId,
        })
      );

      // Give the cart drawer a hint to open if it is present
      const cartToggle = document.querySelector('[data-cart-drawer-toggle], a[href="/cart"]');
      if (cartToggle && cartToggle instanceof HTMLElement) {
        cartToggle.dispatchEvent(new Event('click', { bubbles: true }));
      }
    } catch (err) {
      this.#showError('Network error. Please try again.');
      console.error(err);
    }
  }

  dispatchCartError(json) {
    try {
      document.dispatchEvent(
        new CartErrorEvent('srt-sticky-atc', json.message, json.description, json.errors)
      );
    } catch (_) {
      // no-op
    }
  }
}

document.addEventListener('DOMContentLoaded', () => new SrtStickyATC());
